import React, { useMemo, useRef, useState } from "react";
import {
  AlertMessage,
  IAlertMessageProps,
  QuestionTooltip,
  Spinner,
} from "component-library";

import { useMutation, useQuery, useQueryClient } from "react-query";
import { Formik, FormikProps } from "formik";
import { object, string, date, array, boolean } from "yup";

import { TagminMainOnTransparentImage as tagminMainOnTransparentPng } from "assets";
import { cleanHtml } from "shared/utils/dom";

import AvailabilityCalendarService from "services/AvailabilityCalendarService";

import { availabilityCalendarFallback } from "app/store/fallbackTranslations";

import { AvailabilityCalendarRow } from "../AvailabilityCalendarRow";
import { AvailabilityCalendarForm } from "../AvailabilityCalendarForm";
import { TRACKING_EVENT_TYPE, pushToDataLayer } from "app/helpers/tracking";
import { UserClaimTypes, UserManager } from "app/core";

interface FormFields {
  title: string;
  selectedUnavailableDates: Date[] | undefined;
  // isTagminSelected: boolean;
}

const unavailableListHelperProps = {
  title: availabilityCalendarFallback.unavailableListTooltip.header,
  html: availabilityCalendarFallback.unavailableListTooltip.content,
};

interface IAvailabilityCalendarProps {
  closePopup: () => void;
}

const AVAILABILITY_CALENDAR_QUERY_STRING = "availability-calendar";
const ONE_MIN_IN_MILLISECONDS = 60000;

const AvailabilityCalenderFormSchema = object().shape({
  selectedUnavailableDates: array().length(2).required("Required").of(date()),
  title: string()
    .max(50, availabilityCalendarFallback.titleMaxLengthMessage)
    .required("Required"),
  isTagminSelected: boolean(),
});

const AvailabilityCalendar: React.FC<
  React.PropsWithChildren<IAvailabilityCalendarProps>
> = ({ closePopup }) => {
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);

  const queryClient = useQueryClient();
  const formRef = useRef<FormikProps<FormFields> | null>(null);

  const getUnavailableDates = async () =>
    await AvailabilityCalendarService.getAvailabilityCalendar();
  const updateUnavailableDates = async (body) =>
    await AvailabilityCalendarService.createAvailabilityCalendarItem(body);
  const deleteUnavailableDates = async (id) =>
    await AvailabilityCalendarService.deleteAvailabilityCalendarItem(id);

  const {
    data: unavailableDatesData,
    isLoading: isGetLoading,
    error: getError,
  } = useQuery(AVAILABILITY_CALENDAR_QUERY_STRING, getUnavailableDates);

  const {
    mutate: updateMutation,
    isLoading: isUpdateLoading,
    error: updateError,
  } = useMutation(updateUnavailableDates, {
    onSuccess: () => {
      queryClient.invalidateQueries(AVAILABILITY_CALENDAR_QUERY_STRING);
    },
  });
  const {
    mutate: deleteMutation,
    isLoading: isDeleteLoading,
    error: deleteError,
  } = useMutation(deleteUnavailableDates, {
    onSuccess: () => {
      queryClient.invalidateQueries(AVAILABILITY_CALENDAR_QUERY_STRING);
    },
  });

  const isLoading = isGetLoading || isUpdateLoading || isDeleteLoading;

  const error = getError || updateError || deleteError;

  const handleDeleteDate = (id: string) => {
    if (!unavailableDatesData) {
      return;
    }

    deleteMutation(id);
  };

  const triggerFormikSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const handleAddDates = (values: FormFields) => {
    const {
      selectedUnavailableDates,
      title,
      // isTagminSelected
    } = values;

    if (
      !selectedUnavailableDates ||
      selectedUnavailableDates?.length < 2 ||
      !unavailableDatesData ||
      !title
    ) {
      return;
    }

    const formatDate = (date: Date) => {
      // timezone offset meant that the date was being set to the day before when converted to utc via toISOString. This is a workaround.
      const tzOffset = date.getTimezoneOffset() * ONE_MIN_IN_MILLISECONDS; // getTimezoneOffset returns offset from UTC in mins
      const localISOTime = new Date(date.getTime() - tzOffset)
        .toISOString()
        .slice(0, -1);

      return `${localISOTime}Z`;
    };

    // when you face it replace it with datePickerUniversalDateFormat from component-library please
    // for the sake of time I'm skipping it because of graduates
    const formattedStartDate = formatDate(selectedUnavailableDates[0]);
    const formattedEndDate = formatDate(selectedUnavailableDates[1]);

    const newUnavailableData = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      title: title,
      note: "",
      // externalShared: isTagminSelected,
      externalShared: false,
    };

    updateMutation(newUnavailableData);

    pushToDataLayer(TRACKING_EVENT_TYPE.AddAvailabilityDates, {
      memberType: UserManager.getClaim(UserClaimTypes.role),
    });
  };

  const sortedUnavailableData = useMemo(() => {
    if (!unavailableDatesData) {
      return;
    }

    const formatUnixDate = (date) => new Date(date).getTime();

    return unavailableDatesData.unavailable.sort(
      (a, b) => formatUnixDate(a.startDate) - formatUnixDate(b.startDate)
    );
  }, [unavailableDatesData]);

  const alertProps: IAlertMessageProps = {
    texts: {
      title: availabilityCalendarFallback.title,
    },
    buttons: [
      {
        name: availabilityCalendarFallback.cancelButton,
        type: "secondary",
        click: closePopup,
      },
      {
        name: availabilityCalendarFallback.submitCtaButton,
        type: "primary",
        ctaDisabled: isSubmitButtonDisabled,
        click: triggerFormikSubmit,
      },
    ],
    buttonAlignment: "right",
  };

  const sanitizedTagminLabel = useMemo(() => {
    return cleanHtml(
      availabilityCalendarFallback.tagminCheckboxLabel.replace(
        "{image}",
        `<img src="${tagminMainOnTransparentPng}" alt="Tagmin Logo" class="object-contain pt-1" />`
      )
    );
  }, []);

  const renderUnavailableDatesList = () => {
    if (isLoading) {
      return <Spinner inline />;
    }

    if (error) {
      // errors already caught within main error boundary -- this is simply to replace the spinner
      return (
        <p className="text-large">
          {availabilityCalendarFallback.errorPopupTitle}
        </p>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        {sortedUnavailableData && sortedUnavailableData?.length > 0
          ? sortedUnavailableData.map((data) => (
              <AvailabilityCalendarRow
                key={data.id}
                data={data}
                onClickDelete={handleDeleteDate}
              />
            ))
          : availabilityCalendarFallback.unavailableListEmpty}
      </div>
    );
  };

  return (
    <AlertMessage {...alertProps}>
      <p className="m-0">{availabilityCalendarFallback.explanation}</p>
      <Formik<FormFields>
        initialValues={{
          selectedUnavailableDates: undefined,
          title: "",
          // isTagminSelected: false,
        }}
        onSubmit={(values, { resetForm }) => {
          handleAddDates(values);
          resetForm();
        }}
        validationSchema={AvailabilityCalenderFormSchema}
        innerRef={formRef}
      >
        {(props) => {
          setIsSubmitButtonDisabled(!props.isValid || !props.dirty);

          return (
            <AvailabilityCalendarForm
              sanitizedTagminLabel={sanitizedTagminLabel}
            />
          );
        }}
      </Formik>
      <div className="h-px mx-0 my-4 bg-grey-light-2"></div>
      <div className="flex flex-col gap-2">
        <div className="flex justify-start gap-2">
          <p className="font-semibold">
            {availabilityCalendarFallback.datesListTitle}
          </p>
          <QuestionTooltip {...unavailableListHelperProps} />
        </div>
        {renderUnavailableDatesList()}
      </div>
    </AlertMessage>
  );
};

export default AvailabilityCalendar;
